import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  OnChanges,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { LazyVideoSourceDirective } from '../../../directives/lazy-video-source.directive';
import { LightboxComponent } from '../lightbox/lightbox.component';
import {
  Gallery,
  GalleryMasonryFrame,
  GalleryMasonryGap,
  GalleryMasonryHover,
  GalleryMasonryRhythm,
  GalleryItemView,
} from '../../../../features/projects/interfaces/project.interface';
import { buildGalleryViewModel, resolveShowLightboxThumbnails } from '../../../utils/gallery.utils';
import { GalleryLightboxState } from '../gallery-lightbox.state';

@Component({
  selector: 'app-gallery-masonry',
  standalone: true,
  imports: [CommonModule, LightboxComponent, LazyVideoSourceDirective],
  templateUrl: './gallery-masonry.component.html',
  styleUrl: './gallery-masonry.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GalleryMasonryComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input() gallery!: Gallery;
  @Input() assetBase = '';
  @Input() videoBase = '';
  @Input() showGalleryHeading = true;

  @ViewChildren('cellRef') cells!: QueryList<ElementRef<HTMLElement>>;

  readonly lightbox = new GalleryLightboxState();

  private revealObserver?: IntersectionObserver;

  constructor(
    private cdr: ChangeDetectorRef,
    private readonly ngZone: NgZone,
  ) {}

  public get showLightboxThumbnails(): boolean {
    return resolveShowLightboxThumbnails(this.gallery, this.items.length);
  }

  public get headerLabel(): string {
    const label = this.gallery?.ctaLabel?.trim() || '';
    return label === 'Open premium gallery' ? '' : label;
  }

  public get displayOrderLabel(): string {
    return String((this.gallery?.displayOrder ?? 0) + 1).padStart(2, '0');
  }

  public get masonryShellClasses(): string[] {
    return [
      `masonry-shell--frame-${this.normalizeMasonryFrame(this.gallery?.masonryFrame)}`,
      `masonry-shell--hover-${this.normalizeMasonryHover(this.gallery?.masonryHover)}`,
      `masonry-shell--rhythm-${this.normalizeMasonryRhythm(this.gallery?.masonryRhythm)}`,
    ];
  }

  public get masonryGapClass(): string {
    return `masonry-gap--${this.normalizeMasonryGap(this.gallery?.masonryGap)}`;
  }

  public openAt(index: number): void {
    this.lightbox.openAt(index);
    this.cdr.markForCheck();
  }

  public onLightboxClose(): void {
    this.lightbox.close();
    this.cdr.markForCheck();
  }

  public onLightboxIndexChange(index: number): void {
    this.lightbox.setIndex(index);
    this.cdr.markForCheck();
  }

  public getRevealDelay(index: number): string {
    return `${Math.min((index % 6) * 70, 350)}ms`;
  }

  public ngAfterViewInit(): void {
    this.cells.changes.subscribe(() => this.setupReveal());
    this.setupReveal();
  }

  public ngOnDestroy(): void {
    this.revealObserver?.disconnect();
    this.lightbox.close();
  }

  public items: GalleryItemView[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['gallery'] || changes['assetBase'] || changes['videoBase']) {
      const viewModel = buildGalleryViewModel(this.gallery, {
        assetBase: this.assetBase,
        videoBase: this.videoBase,
        includeMasonryClasses: true,
        masonryRhythm: this.normalizeMasonryRhythm(this.gallery?.masonryRhythm),
      });
      this.items = viewModel.items;
      this.lightbox.close();
    }
  }

  private normalizeMasonryRhythm(value: unknown): GalleryMasonryRhythm {
    const valid = new Set<GalleryMasonryRhythm>(['balanced', 'hero', 'editorial']);
    return valid.has(value as GalleryMasonryRhythm) ? (value as GalleryMasonryRhythm) : 'editorial';
  }

  private normalizeMasonryGap(value: unknown): GalleryMasonryGap {
    const valid = new Set<GalleryMasonryGap>(['none', 'hairline', 'tight', 'compact', 'standard', 'relaxed', 'generous', 'roomy']);
    return valid.has(value as GalleryMasonryGap) ? (value as GalleryMasonryGap) : 'standard';
  }

  private normalizeMasonryFrame(value: unknown): GalleryMasonryFrame {
    const valid = new Set<GalleryMasonryFrame>(['soft', 'museum', 'edge']);
    return valid.has(value as GalleryMasonryFrame) ? (value as GalleryMasonryFrame) : 'edge';
  }

  private normalizeMasonryHover(value: unknown): GalleryMasonryHover {
    const valid = new Set<GalleryMasonryHover>(['lift', 'still', 'zoom']);
    return valid.has(value as GalleryMasonryHover) ? (value as GalleryMasonryHover) : 'zoom';
  }

  private setupReveal(): void {
    this.revealObserver?.disconnect();
    const cells = this.cells?.map((cell) => cell.nativeElement) ?? [];
    if (!cells.length) return;

    if (typeof IntersectionObserver === 'undefined') {
      cells.forEach((cell) => cell.classList.add('masonry-cell--in'));
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.revealCell(entry.target as HTMLElement);
            this.revealObserver?.unobserve(entry.target);
          });
        },
        { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
      );

      cells.forEach((cell) => this.revealObserver!.observe(cell));
    });
  }

  private revealCell(cell: HTMLElement): void {
    const image = cell.querySelector<HTMLImageElement>('img.masonry-media');
    if (!image) {
      cell.classList.add('masonry-cell--in');
      return;
    }

    const reveal = (): void => cell.classList.add('masonry-cell--in');
    const revealAfterDecode = (): void => {
      if (typeof image.decode !== 'function') {
        reveal();
        return;
      }

      image.decode().then(reveal).catch(reveal);
    };

    if (image.complete) {
      revealAfterDecode();
      return;
    }

    image.addEventListener('load', revealAfterDecode, { once: true });
    image.addEventListener('error', reveal, { once: true });
  }
}
